import React, { useMemo, useRef } from 'react';
import { toPercent } from 'pano-utils';
import limitNum from '@utils/limitNum';
import { useSliderContext } from '@components/Slider/context';

interface Props {
  step?: number;
}

const WIDTH = 55;

export function AlphaSliderBar({ step = 1 }: Props) {
  const { min, max, value, onChange } = useSliderContext();
  const divRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<number>(value);
  const range = max - min;
  const posLeft = toPercent((value - min) / range);

  const ratio = useMemo(() => {
    const decimal = String(step).split('.')[1];
    if (!decimal) return 1;
    return 10 ** decimal.length;
  }, [step]);

  const getVal = (e: MouseEvent) => {
    if (!divRef.current?.parentElement) return value;
    const { left, right, width } =
      divRef.current.parentElement.getBoundingClientRect();
    const clientX = limitNum({ min: left, max: right, value: e.clientX });
    const roundedVal = Math.round((((clientX - left) / width) * range) / step);
    return Math.round((roundedVal * step + min) * ratio) / ratio;
  };

  const handleMouseMove = (e: MouseEvent) => {
    const newVal = getVal(e);
    if (onChange && prevRef.current !== newVal) {
      onChange(newVal);
    }
    prevRef.current = newVal;
  };

  const handleMouseUp = () => {
    document.removeEventListener('mouseup', handleMouseUp, false);
    document.removeEventListener('mousemove', handleMouseMove, false);
  };

  const handleMouseDown = () => {
    document.addEventListener('mouseup', handleMouseUp, false);
    document.addEventListener('mousemove', handleMouseMove, false);
  };

  const leftText = Math.round(value * 100);
  const rightText = 100 - leftText;

  return (
    <div className="relative flex h-20 w-full items-center justify-center">
      <div className="absolute h-4 w-full rounded-full bg-white/30" />
      <div
        className="absolute h-full"
        style={{ width: `calc(100% - ${WIDTH}px)` }}
      >
        <div
          ref={divRef}
          className="absolute flex cursor-pointer select-none items-center justify-center rounded-full bg-white text-sm text-black"
          style={{ width: WIDTH, left: `calc(${posLeft} - ${WIDTH / 2}px)` }}
          onMouseDown={handleMouseDown}
        >
          {`${leftText}:${rightText}`}
        </div>
      </div>
    </div>
  );
}
